import { useEffect, useState } from 'react';
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { authPageHref } from '@/lib/redirect';
import { invitationsApi, type InvitationLookup } from '@/lib/api';
import { Users, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

const primaryClass =
  'w-full flex items-center justify-center gap-2 py-2.5 bg-accent text-accent-foreground rounded-full text-sm font-medium hover:bg-accent/90 disabled:opacity-50 transition-colors';

export function InviteAccept() {
  const { token } = useParams<{ token: string }>();
  const { getToken, isSignedIn, isLoaded, user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [invite, setInvite] = useState<InvitationLookup | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // The lookup is public, so people without an account yet still see who
  // invited them before being asked to sign up.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    invitationsApi
      .lookup(null, token)
      .then((data) => { if (!cancelled) setInvite(data.invitation); })
      .catch(() => { if (!cancelled) setError('This invitation is invalid, already used or has expired.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  async function handleAccept() {
    if (!token) return;
    setAccepting(true);
    setError(null);
    try {
      const authToken = await getToken();
      await invitationsApi.accept(authToken!, token);
      setSuccess(true);
      setTimeout(() => navigate('/'), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept invitation.');
    } finally {
      setAccepting(false);
    }
  }

  async function switchAccount() {
    await signOut();
    navigate(authPageHref('/sign-in', location.pathname, invite?.email), { replace: true });
  }

  const wrongAccount =
    !!invite && !!user && user.email.toLowerCase() !== invite.email.toLowerCase();

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-xl p-8 max-w-sm w-full text-center shadow-xl">
        <div className="h-12 w-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
          <Users className="h-6 w-6 text-accent" />
        </div>

        {loading || !isLoaded ? (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Loading invitation…</p>
          </div>
        ) : success ? (
          <>
            <CheckCircle2 className="h-10 w-10 text-success mx-auto mb-3" />
            <h1 className="text-lg font-semibold text-foreground mb-2">Welcome aboard!</h1>
            <p className="text-sm text-muted-foreground">
              You've joined {invite?.agency_name}. Redirecting you to the dashboard…
            </p>
          </>
        ) : !invite ? (
          <>
            <AlertCircle className="h-10 w-10 text-destructive mx-auto mb-3" />
            <h1 className="text-lg font-semibold text-foreground mb-2">Invitation error</h1>
            <p className="text-sm text-destructive">{error}</p>
            <p className="text-xs text-muted-foreground mt-4">
              Ask the person who invited you to send a new invitation.
            </p>
          </>
        ) : (
          <>
            <h1 className="text-lg font-semibold text-foreground mb-2">You're invited!</h1>
            <p className="text-sm text-muted-foreground mb-1">You've been invited to join</p>
            <p className="text-base font-bold text-foreground mb-1">{invite.agency_name}</p>
            <p className="text-xs text-muted-foreground mb-6">
              as <span className="capitalize font-medium text-foreground">{invite.role}</span>
              {' · '}
              <span className="font-medium text-foreground">{invite.email}</span>
            </p>

            {!isSignedIn ? (
              <>
                <Link to={authPageHref('/sign-up', location.pathname, invite.email)} className={primaryClass}>
                  Create your account
                </Link>
                <p className="text-xs text-muted-foreground mt-4">
                  Already have an Enflux account?{' '}
                  <Link
                    to={authPageHref('/sign-in', location.pathname, invite.email)}
                    className="text-accent font-medium hover:underline"
                  >
                    Sign in
                  </Link>
                </p>
              </>
            ) : wrongAccount ? (
              <>
                <p className="text-sm text-warning mb-4">
                  You're signed in as <span className="font-medium">{user!.email}</span>, but this
                  invitation is for <span className="font-medium">{invite.email}</span>.
                </p>
                <button onClick={switchAccount} className={primaryClass}>
                  Switch account
                </button>
              </>
            ) : (
              <button onClick={handleAccept} disabled={accepting} className={primaryClass}>
                {accepting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Accept Invitation
              </button>
            )}

            {error && <p className="text-sm text-destructive mt-4">{error}</p>}
            <p className="text-[10px] text-muted-foreground mt-3">
              Expires {new Date(invite.expires_at).toLocaleDateString()}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
